/* eslint-disable @typescript-eslint/no-use-before-define */
import { useEffect, useMemo, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../../../redux/store';
import {
  deleteSupplierInvoice,
  getGroupedSuppliersInvoices,
  supplierDownloadSupplierInvoiceStatements,
} from '../../../../redux/services/invoicesService';
import {
  AppFile,
  Buyer,
  DillInvoice,
  DocumentsSummary,
  QuickbooksCreditMemo,
  QuickbooksCreditMemoSchema,
  QuickbooksInvoice,
  QuickbooksInvoiceSchema,
  QuickbooksJob,
  SupplierForBuyer,
  formatCollectionActivityLine,
  formatMoney,
  getCurrentTimezoneDate,
  removeDuplicatesByProperty,
  roundTo,
} from '@dill/dill-shared';
import { twMerge } from 'tailwind-merge';
import moment from 'moment';
import { DillButton, DillColumn, DillTable } from '../../../general/Dill';
import { ListChecks, FilePlus, CaretDown, Warning } from 'phosphor-react';
import { Download, Eye, Mail } from 'react-feather';
import { COLORS } from '../../../../utils/colors';
import { downloadFile, openDillToast } from '../../../../utils/helpers';
import {
  sendInvoicePaymentReminderEmail,
  sendBulkInvoicePaymentReminderEmails,
} from '../../../../redux/services/paymentsService';
import { AppPdfPreviewModal } from '../../../general/AppPdfPreviewModal/AppPdfPreviewModal';
import AppViewInvoiceWithoutPdfModal from '../../../general/AppViewInvoiceWithoutPdfModal/AppViewInvoiceWithoutPdfModal';
import { getDocumentPdf } from '../../../../redux/services/buyersService';
import { logEvent } from 'firebase/analytics';
import { myAnalytics } from '../../../../firebase/config';
import InvoiceActivitiesModal from './InvoiceActivitiesModal';
import AppCustomizePaymentReminderModal from '../../../general/AppCustomizePaymentReminderModal/AppCustomizePaymentReminderModal';
import AppSendStatementModal from '../../../general/AppSendStatementModal/AppSendStatementModal';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import {
  selectCreditMemo,
  selectInvoice,
  unSelectCreditMemo,
  unSelectInvoice,
} from '../../../../redux/globalSlices/invoiceSlice';
import {
  selectInvoiceIdsForDownload,
  unSelectInvoiceIdsForDownload,
} from '../../../../redux/globalSlices/buyersSlice';
import AppSelectedPayInvoicesModal from '../../../general/AppSelectedPayInvoicesModal/AppSelectedPayInvoicesModal';
import { openMessageModal } from '../../../../redux/globalSlices/genericSlice';
import { DillDropdownButton } from '../../../general/Dill/DillDropdownButton';
import { sendEmailStatementsForBuyers } from '../../../../redux/services/suppliersService';
import {
  getBuyerStatementPeriods,
  sendEmailStatementsForBuyersWithStatementId,
} from '../../../../redux/services/supplierBuyersService';
import { getActivePromisesForInvoices } from '../../../../redux/services/promiseToPayService';
import { CreateDisputeModal } from '../../suppliers/SupplierDisputes/CreateDisputeModal';

const Icon = ({ d, size = 16 }: { d: string; size?: number }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round">
    <path d={d} />
  </svg>
);
const TrashIcon = () => <Icon d="M3 6h18M8 6V4h8v2M19 6l-1 14H6L5 6" />;

const CustomerInvoicesTable = ({
  selectedBuyer,
  customerDetails,
  onInvoicesFetched,
  onCustomerRefresh,
  selectedJobIds,
}: {
  selectedBuyer?: Buyer | null;
  customerDetails: {
    buyer: Buyer;
    usersEmailsDetails: {
      email: string;
      userId: string;
      isInvitedUser: boolean;
      isStatementsEmailOptOut: boolean;
      isPaymentRemindersEmailOptOut: boolean;
    }[];
    documentsSummary: DocumentsSummary;
  } | null;
  onInvoicesFetched?: (invoices: (QuickbooksInvoice | QuickbooksCreditMemo)[]) => void;
  onCustomerRefresh?: () => void;
  selectedJobIds: string[];
}) => {
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.auth);
  const currentSupplier = useAppSelector((state) => state.auth.currentSupplier);
  const [currentBuyerInvoices, setCurrentBuyerInvoices] = useState<
    (QuickbooksInvoice | QuickbooksCreditMemo)[]
  >([]);

  const [previewPdfList, setPreviewPdfList] = useState([] as any);
  const [openPdfPreviewModal, setOpenPdfPreviewModal] = useState(false);
  const [openActivitiesModal, setOpenActivitiesModal] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState<QuickbooksInvoice | null>(null);
  const [selectedCreditMemo, setSelectedCreditMemo] = useState<QuickbooksCreditMemo | null>(null);
  const [isViewInvoiceWithoutPdfModalOpen, setIsViewInvoiceWithoutPdfModalOpen] = useState(false);
  const [invoiceForDispute, setInvoiceForDispute] = useState<QuickbooksInvoice | null>(null);
  const [openCreateDisputeModal, setOpenCreateDisputeModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [sort, setSort] = useState('');
  const [sorted, setSorted] = useState(false);
  const [sortedInvoices, setSortedInvoices] = useState<
    (QuickbooksInvoice | QuickbooksCreditMemo)[]
  >([]);
  const { invoiceIdsForDownload } = useAppSelector((state) => state.buyers);
  const { selectedInvoices, selectedCreditMemos } = useAppSelector((state) => state.invoice);
  const { activePromisesByInvoice } = useAppSelector((state) => state.promiseToPays);
  const [invoicesToBePaid, setInvoicesToBePaid] = useState([] as QuickbooksInvoice[]);
  const [payInvoicesModalOpen, setPayInvoicesModalOpen] = useState(false);
  const [pendingCreditMemoIds, setPendingCreditMemoIds] = useState([] as string[]);
  const [openCustomizeReminderModal, setOpenCustomizeReminderModal] = useState(false);
  const [selectedInvoiceForReminder, setSelectedInvoiceForReminder] =
    useState<QuickbooksInvoice | null>(null);
  const [customReminderLoading, setCustomReminderLoading] = useState(false);
  const [openBulkCustomizeReminderModal, setOpenBulkCustomizeReminderModal] = useState(false);
  const [bulkReminderLoading, setBulkReminderLoading] = useState(false);
  const [currentReminderType, setCurrentReminderType] = useState<'PAYMENT' | 'CREDIT_HOLD'>(
    'PAYMENT'
  );
  const [previouslySelectedIds, setPreviouslySelectedIds] = useState<Set<string>>(new Set());
  const [statementPeriods, setStatementPeriods] = useState<{ id: string; statementDate: any }[]>(
    []
  );
  const [openSendStatementModal, setOpenSendStatementModal] = useState(false);
  const [pendingStatementData, setPendingStatementData] = useState<{
    buyerIds: string[];
    statementId?: string;
  } | null>(null);
  const [sendStatementLoading, setSendStatementLoading] = useState(false);

  const handleSelectionChange = (
    rows: (QuickbooksInvoice | QuickbooksCreditMemo)[],
    indices: number[]
  ) => {
    const selectedTabId = 'OPEN';
    const currentSelectedIds = new Set(rows.map((row) => row.id));
    const previousSelectedIds = previouslySelectedIds;

    const deselectedIds = [...previousSelectedIds].filter((id) => !currentSelectedIds.has(id));

    if (deselectedIds.length > 0) {
      const deselectedInvoices: QuickbooksInvoice[] = [];
      const deselectedCreditMemos: QuickbooksCreditMemo[] = [];

      const allInvoices = currentBuyerInvoices.filter((inv) => deselectedIds.includes(inv.id));
      allInvoices.forEach((row) => {
        const invoiceCheck = QuickbooksInvoiceSchema.safeParse(row);
        const creditMemoCheck = QuickbooksCreditMemoSchema.safeParse(row);
        if (invoiceCheck.success) {
          deselectedInvoices.push(invoiceCheck.data);
        } else if (creditMemoCheck.success) {
          deselectedCreditMemos.push(creditMemoCheck.data);
        }
      });

      if (deselectedInvoices.length > 0) {
        dispatch(unSelectInvoice({ tabId: selectedTabId, invoices: deselectedInvoices }));
        dispatch(
          unSelectInvoiceIdsForDownload({
            tabId: selectedTabId,
            invoiceIds: deselectedInvoices.map((inv) => inv.id),
          })
        );
      }
      if (deselectedCreditMemos.length > 0) {
        dispatch(unSelectCreditMemo({ tabId: selectedTabId, creditMemos: deselectedCreditMemos }));
      }
    }

    rows.forEach((row) => {
      const invoiceCheck = QuickbooksInvoiceSchema.safeParse(row);
      const creditMemoCheck = QuickbooksCreditMemoSchema.safeParse(row);
      if (invoiceCheck.success) {
        dispatch(selectInvoiceIdsForDownload({ tabId: selectedTabId, invoiceIds: [row.id] }));
        dispatch(selectInvoice({ tabId: selectedTabId, invoices: [invoiceCheck.data] }));
      } else if (creditMemoCheck.success) {
        dispatch(selectCreditMemo({ tabId: selectedTabId, creditMemos: [creditMemoCheck.data] }));
      }
    });

    setPreviouslySelectedIds(currentSelectedIds);
  };

  const selectedJobs = useMemo(() => {
    if (currentBuyerInvoices) {
      const jobs = currentBuyerInvoices.reduce((prev: QuickbooksJob[], curr) => {
        if (curr.jobId && curr.quickBooksJob) {
          return [...prev, curr.quickBooksJob];
        } else {
          return prev;
        }
      }, []);
      return removeDuplicatesByProperty({ inputArray: jobs, property: 'id' });
    }
    return [];
  }, [currentBuyerInvoices]);

  const fetchData = async (buyerId: string) => {
    const results = await dispatch(
      getGroupedSuppliersInvoices({
        buyerId: buyerId,
        weeksLimit: 12,
        supplierId: currentSupplier?.id || '',
      })
    );
    if (results && results.type === 'suppliers/getGroupedSuppliersInvoices/fulfilled') {
      const payload = results.payload as any;
      if (payload.data.buyerSuppliers) {
        const data = payload.data.buyerSuppliers as SupplierForBuyer[];
        let invoices: (QuickbooksInvoice | QuickbooksCreditMemo)[] = [];
        const creditIds: string[] = [];
        data.forEach((item) => {
          invoices = [...invoices, ...(item.invoices?.pastDueInvoices.invoices || [])];
          invoices = [...invoices, ...(item.invoices?.currentInvoices.invoices || [])];
          invoices = [...invoices, ...(item.invoices?.pendingPayInvoices.invoices || [])];
          invoices = [...invoices, ...(item.invoices?.creditMemosDetails.creditMemos || [])];
          invoices = [...invoices, ...(item.invoices?.creditMemosDetails.pendingCreditMemos || [])];
          invoices = [
            ...invoices,
            ...(item.invoices?.paidInvoices.invoices
              ? item.invoices?.paidInvoices.invoices.filter((invoice) => invoice.dillPaymentStatus)
              : []),
          ];
          const pendingCreditMemos = item.invoices?.creditMemosDetails.pendingCreditMemos || [];
          pendingCreditMemos.forEach((creditMemo) => {
            creditIds.push(creditMemo.id);
          });
        });
        setCurrentBuyerInvoices(invoices);
        setPendingCreditMemoIds(creditIds);
        onInvoicesFetched?.(invoices);
        // Fetch active promise-to-pay data for the loaded invoices
        const invoiceIds = invoices
          .filter((inv) => QuickbooksInvoiceSchema.safeParse(inv).success)
          .map((inv) => inv.id);
        if (invoiceIds.length > 0 && currentSupplier?.id) {
          dispatch(
            getActivePromisesForInvoices({
              supplierId: currentSupplier.id,
              invoiceIds,
            })
          );
        }
      } else {
        setCurrentBuyerInvoices([]);
      }
    } else {
      setCurrentBuyerInvoices([]);
    }
  };
  useEffect(() => {
    if (selectedBuyer) {
      setCurrentBuyerInvoices([]);
      fetchData(selectedBuyer?.id);
    }
    setLoading(false);

    return () => {};
  }, [selectedBuyer]);

  useEffect(() => {
    if (customerDetails?.buyer?.id && currentSupplier?.id) {
      dispatch(
        getBuyerStatementPeriods({
          supplierId: currentSupplier.id,
          buyerId: customerDetails.buyer.id,
        })
      ).then((res) => {
        if (res.type === 'supplierBuyers/getBuyerStatementPeriods/fulfilled') {
          const payload = res.payload as any;
          setStatementPeriods(payload?.data?.statementPeriods ?? []);
        }
      });
    } else {
      setStatementPeriods([]);
    }
  }, [customerDetails?.buyer?.id]);

  const filteredCurrentBuyerInvoices = useMemo(() => {
    let finalSearchedInvoices = [];
    if (currentBuyerInvoices) {
      const searched = currentBuyerInvoices.filter((documentDetails) => {
        const invoiceCheck = QuickbooksInvoiceSchema.safeParse(documentDetails);
        const creditMemoCheck = QuickbooksCreditMemoSchema.safeParse(documentDetails);
        let invoice: QuickbooksInvoice | null = null;
        let creditMemo: QuickbooksCreditMemo | null = null;
        if (invoiceCheck.success) {
          invoice = invoiceCheck.data;
        }
        if (creditMemoCheck.success) {
          creditMemo = creditMemoCheck.data;
        }
        let filters: {
          isMatchTextFilter?: boolean;
          isMatchJobFilter?: boolean;
          isMatchInvoiceFilter?: boolean;
        } = {};
        if (searchText) {
          if (
            (invoice &&
              ((invoice?.quickBooksJob?.jobName &&
                invoice?.quickBooksJob?.jobName
                  .toString()
                  .toLowerCase()
                  .includes(searchText.toLowerCase())) ||
                (invoice?.quickBooksJob?.address &&
                  invoice?.quickBooksJob?.address
                    .toString()
                    .toLowerCase()
                    .includes(searchText.toLowerCase())) ||
                (invoice?.quickBooksJob?.jobNumber &&
                  invoice?.quickBooksJob?.jobNumber
                    .toString()
                    .toLowerCase()
                    .includes(searchText.toLowerCase())) ||
                (invoice?.poNumber &&
                  invoice?.poNumber.toString().toLowerCase().includes(searchText.toLowerCase())) ||
                (invoice?.amount &&
                  Number(invoice?.amount)
                    .toFixed(2)
                    .toString()
                    .includes(searchText.toLowerCase())) ||
                (invoice?.terms &&
                  invoice?.terms.toString().toLowerCase().includes(searchText.toLowerCase())) ||
                (invoice?.dueDate &&
                  moment(invoice?.dueDate)
                    .format('MM/DD/YYYY')
                    .toLowerCase()
                    .includes(searchText.toLowerCase())) ||
                (invoice?.createdAt &&
                  moment(invoice?.createdAt)
                    .format('MM/DD/YYYY')
                    .toLowerCase()
                    .includes(searchText.toLowerCase())) ||
                (invoice?.docNumber &&
                  invoice?.docNumber.toString().toLowerCase().includes(searchText.toLowerCase())) ||
                (invoice?.balance &&
                  Number(invoice?.balance)
                    .toFixed(2)
                    .toString()
                    .includes(searchText.toLowerCase())) ||
                (invoice?.recordType &&
                  invoice?.recordType
                    .toString()
                    .toLowerCase()
                    .replace('_', ' ')
                    .includes(searchText.toLowerCase())))) ||
            (creditMemo &&
              ((creditMemo?.quickBooksJob?.jobName &&
                creditMemo?.quickBooksJob?.jobName
                  .toString()
                  .toLowerCase()
                  .includes(searchText.toLowerCase())) ||
                (creditMemo?.quickBooksJob?.address &&
                  creditMemo?.quickBooksJob?.address
                    .toString()
                    .toLowerCase()
                    .includes(searchText.toLowerCase())) ||
                (creditMemo?.quickBooksJob?.jobNumber &&
                  creditMemo?.quickBooksJob?.jobNumber
                    .toString()
                    .toLowerCase()
                    .includes(searchText.toLowerCase())) ||
                (creditMemo?.docNumber &&
                  creditMemo?.docNumber
                    .toString()
                    .toLowerCase()
                    .includes(searchText.toLowerCase())) ||
                (creditMemo?.totalAmount &&
                  Number(creditMemo?.totalAmount)
                    .toFixed(2)
                    .toString()
                    .includes(searchText.toLowerCase())) ||
                (creditMemo?.jobName &&
                  creditMemo?.jobName
                    .toString()
                    .toLowerCase()
                    .includes(searchText.toLowerCase())) ||
                (creditMemo?.createdAt &&
                  moment(creditMemo?.createdAt)
                    .format('MM/DD/YYYY')
                    .toLowerCase()
                    .includes(searchText.toLowerCase())) ||
                (creditMemo?.remainingCredit &&
                  Number(creditMemo?.remainingCredit)
                    .toFixed(2)
                    .toString()
                    .includes(searchText.toLowerCase())) ||
                (creditMemo?.recordType &&
                  creditMemo?.recordType
                    .toString()
                    .toLowerCase()
                    .replace('_', ' ')
                    .includes(searchText.toLowerCase()))))
          ) {
            filters = { ...filters, isMatchTextFilter: true };
          } else {
            filters = { ...filters, isMatchTextFilter: false };
          }
        }
        if (selectedJobIds.length > 0) {
          if (invoice && invoice?.quickBooksJob) {
            filters = {
              ...filters,
              isMatchJobFilter:
                invoice?.quickBooksJob && selectedJobIds?.includes(invoice.quickBooksJob.id),
            };
          } else if (creditMemo && creditMemo?.quickBooksJob) {
            filters = {
              ...filters,
              isMatchJobFilter:
                creditMemo?.quickBooksJob && selectedJobIds?.includes(creditMemo.quickBooksJob.id),
            };
          } else {
            filters = {
              ...filters,
              isMatchJobFilter: false,
            };
          }
        }

        return Object.values(filters).every((item) => item);
      });
      finalSearchedInvoices = searched;
    } else {
      finalSearchedInvoices = currentBuyerInvoices;
    }
    return finalSearchedInvoices;
  }, [currentBuyerInvoices, searchText, selectedJobIds]);

  const handleSendInvoicePaymentReminder = async (
    invoiceId: string,
    reminderType?: 'PAYMENT' | 'CREDIT_HOLD'
  ) => {
    if (selectedBuyer) {
      const results = await dispatch(
        sendInvoicePaymentReminderEmail({
          supplierId: currentSupplier?.id || '',
          buyerId: selectedBuyer?.id,
          invoiceId,
          reminderType,
        })
      );
      if (results.type === 'payments/sendInvoicePaymentReminderEmail/fulfilled') {
        openDillToast({
          type: 'SUCCESS',
          message:
            reminderType === 'CREDIT_HOLD'
              ? 'Credit hold reminder sent!'
              : 'Payment reminder sent!',
        });
        fetchData(selectedBuyer?.id);
      } else {
        const payload = results.payload as any;
        let errorMessage =
          reminderType === 'CREDIT_HOLD'
            ? 'Error sending credit hold reminder!'
            : 'Error sending payment reminder!';
        if (payload) {
          errorMessage = payload;
        }
        openDillToast({ type: 'ERROR', message: errorMessage });
      }
    }
  };

  const handleSendCustomizedPaymentReminder = async (
    data: {
      subjectLine: string;
      notes: string;
      attachments: File[];
      excludedUserIds: string[];
      additionalEmails: string[];
      includeInvoicePdf: boolean;
      mergeInvoicePdf: boolean;
    },
    reminderType?: 'PAYMENT' | 'CREDIT_HOLD'
  ) => {
    if (selectedBuyer && selectedInvoiceForReminder) {
      setCustomReminderLoading(true);
      const results = await dispatch(
        sendInvoicePaymentReminderEmail({
          supplierId: currentSupplier?.id || '',
          buyerId: selectedBuyer?.id,
          invoiceId: selectedInvoiceForReminder.id,
          subjectLine: data.subjectLine,
          notes: data.notes,
          attachments: data.attachments,
          reminderType,
          excludedUserIds: data.excludedUserIds,
          additionalEmails: data.additionalEmails,
          includeInvoicePdf: data.includeInvoicePdf,
          mergeInvoicePdf: data.mergeInvoicePdf,
        })
      );
      setCustomReminderLoading(false);
      if (results.type === 'payments/sendInvoicePaymentReminderEmail/fulfilled') {
        openDillToast({
          type: 'SUCCESS',
          message:
            reminderType === 'CREDIT_HOLD'
              ? 'Customized credit hold reminder sent!'
              : 'Customized payment reminder sent!',
        });
        setOpenCustomizeReminderModal(false);
        setSelectedInvoiceForReminder(null);
        fetchData(selectedBuyer?.id);
        if (data.additionalEmails.length > 0) {
          onCustomerRefresh?.();
        }
      } else {
        const payload = results.payload as any;
        let errorMessage =
          reminderType === 'CREDIT_HOLD'
            ? 'Error sending customized credit hold reminder!'
            : 'Error sending customized payment reminder!';
        if (payload) {
          errorMessage = payload;
        }
        openDillToast({ type: 'ERROR', message: errorMessage });
      }
    }
  };

  const handleSendBulkInvoicePaymentReminder = async (reminderType?: 'PAYMENT' | 'CREDIT_HOLD') => {
    const flatSelectedInvoices = Object.values(selectedInvoices).flat();
    if (selectedBuyer && flatSelectedInvoices.length > 0) {
      setBulkReminderLoading(true);
      const results = await dispatch(
        sendBulkInvoicePaymentReminderEmails({
          supplierId: currentSupplier?.id || '',
          buyerId: selectedBuyer?.id,
          invoiceIds: flatSelectedInvoices.map((inv) => inv.id),
          reminderType,
        })
      );
      setBulkReminderLoading(false);
      if (results.type === 'payments/sendBulkInvoicePaymentReminderEmails/fulfilled') {
        openDillToast({
          type: 'SUCCESS',
          message:
            reminderType === 'CREDIT_HOLD'
              ? 'Bulk credit hold reminder sent!'
              : 'Bulk payment reminder sent!',
        });
        fetchData(selectedBuyer?.id);
      } else {
        const payload = results.payload as any;
        let errorMessage =
          reminderType === 'CREDIT_HOLD'
            ? 'Error sending bulk credit hold reminder!'
            : 'Error sending bulk payment reminder!';
        if (payload) {
          errorMessage = payload;
        }
        openDillToast({ type: 'ERROR', message: errorMessage });
      }
    }
  };

  const handleSendBulkCustomizedPaymentReminder = async (
    data: {
      subjectLine: string;
      notes: string;
      attachments: File[];
      excludedUserIds: string[];
      additionalEmails: string[];
      includeInvoicePdf: boolean;
      mergeInvoicePdf: boolean;
    },
    reminderType?: 'PAYMENT' | 'CREDIT_HOLD'
  ) => {
    const flatSelectedInvoices = Object.values(selectedInvoices).flat();
    if (selectedBuyer && flatSelectedInvoices.length > 0) {
      setBulkReminderLoading(true);
      const results = await dispatch(
        sendBulkInvoicePaymentReminderEmails({
          supplierId: currentSupplier?.id || '',
          buyerId: selectedBuyer?.id,
          invoiceIds: flatSelectedInvoices.map((inv) => inv.id),
          subjectLine: data.subjectLine,
          notes: data.notes,
          attachments: data.attachments,
          reminderType,
          excludedUserIds: data.excludedUserIds,
          additionalEmails: data.additionalEmails,
          includeInvoicePdf: data.includeInvoicePdf,
          mergeInvoicePdf: data.mergeInvoicePdf,
        })
      );
      setBulkReminderLoading(false);
      if (results.type === 'payments/sendBulkInvoicePaymentReminderEmails/fulfilled') {
        openDillToast({
          type: 'SUCCESS',
          message:
            reminderType === 'CREDIT_HOLD'
              ? 'Bulk customized credit hold reminder sent!'
              : 'Bulk customized payment reminder sent!',
        });
        setOpenBulkCustomizeReminderModal(false);
        fetchData(selectedBuyer?.id);
        if (data.additionalEmails.length > 0) {
          onCustomerRefresh?.();
        }
      } else {
        const payload = results.payload as any;
        let errorMessage =
          reminderType === 'CREDIT_HOLD'
            ? 'Error sending bulk customized credit hold reminder!'
            : 'Error sending bulk customized payment reminder!';
        if (payload) {
          errorMessage = payload;
        }
        openDillToast({ type: 'ERROR', message: errorMessage });
      }
    }
  };
  const handleDownloadSelectedPdf = (url: string) => {
    downloadFile(url, `invoice-${selectedInvoice?.docNumber}-${+new Date()}.pdf`);
  };

  const handleDownloadPdf = async ({
    type,
    id,
    docNumber,
    signedPdfs,
  }: {
    type: 'INVOICE' | 'CREDIT_MEMO';
    id: string;
    docNumber: string;
    signedPdfs?: AppFile[] | undefined | null;
  }) => {
    if (signedPdfs && signedPdfs.length > 0) {
      const sortedPdfs = [...signedPdfs].sort((a, b) => {
        if (!b.date || !a.date) {
          return 0;
        }
        return new Date(b.date.toString()).getTime() > new Date(a.date.toString()).getTime()
          ? 1
          : -1;
      });
      if (sortedPdfs.length > 1) {
      } else {
        downloadFile(sortedPdfs[0].url || '', `invoice-${docNumber}-${+new Date()}.pdf`);
        logEvent(myAnalytics, 'buyer_download_invoice', {
          userId: user ? user.id : '',
          time: new Date(),
          route: location.pathname,
        });
      }
    } else {
      const res = await dispatch(
        getDocumentPdf({
          supplierDomainId: currentSupplier?.domainId,
          id,
          type,
        })
      );
      if (res && res.type === 'buyers/getDocumentPdf/fulfilled') {
        openDillToast({ message: 'invoice pdf successfully downloaded', type: 'SUCCESS' });
      } else {
        openDillToast({ message: 'Failed to download invoice pdf', type: 'ERROR' });
      }

      logEvent(myAnalytics, 'buyer_download_invoice', {
        userId: user ? user.id : '',
        time: new Date(),
        route: location.pathname,
      });
    }
  };

  const handlePreviewPdf = async ({
    type,
    id,
    docNumber,
    signedPdfs,
  }: {
    type: 'INVOICE' | 'CREDIT_MEMO';
    id: string;
    docNumber: string;
    signedPdfs?: AppFile[] | undefined | null;
  }) => {
    if (signedPdfs && signedPdfs.length > 0) {
      const sortedPdfs = [...signedPdfs].sort((a, b) => {
        if (!b.date || !a.date) {
          return 0;
        }
        return new Date(b.date.toString()).getTime() > new Date(a.date.toString()).getTime()
          ? 1
          : -1;
      });
      setPreviewPdfList(sortedPdfs);
      setOpenPdfPreviewModal(true);
      logEvent(myAnalytics, 'buyer_preview_invoice', {
        userId: user ? user.id : '',
        time: new Date(),
        route: location.pathname,
      });
    } else {
      const invoice = await dispatch(
        getDocumentPdf({
          supplierDomainId: currentSupplier?.domainId,
          id,
          type,
          preview: true,
        })
      );
      if (invoice && invoice.type === 'buyers/getDocumentPdf/fulfilled') {
        //
      } else {
        openDillToast({ message: 'Failed to get pdf', type: 'ERROR' });
      }
      if (invoice.payload) {
        const payload = invoice.payload as any;
        if (payload?.data?.pdfUrl) {
          setPreviewPdfList([
            {
              url: payload.data.pdfUrl,
              name: 'pdf',
              date: new Date(),
            },
          ]);
          setOpenPdfPreviewModal(true);
        }
      }
    }
  };

  const handlePreviewInvoiceWithoutPdf = (invoice: QuickbooksInvoice) => {
    setSelectedInvoice(invoice);
    setIsViewInvoiceWithoutPdfModalOpen(true);
  };

  const columns = useMemo<DillColumn<QuickbooksInvoice | QuickbooksCreditMemo>[]>(() => {
    const supplier = currentSupplier;
    const NET_TERMS_REGEX = /^(\d+%\s?.*|.*\s?\d+%)$/;
    const CVS_PLUMBING_DISCOUNT_TERMS = ['210N25', '210N30', '210N30X15'];
    const DISCOUNT_INTEGRATIONS = [
      'MANUAL_UPLOAD',
      'DDI_MANUAL_UPLOAD',
      'P21_MANUAL_UPLOAD',
      'SAGE_100',
      'DISTRIBUTION_ONE',
      'RUTTER_QBD',
      'ECLIPSE',
      'ECLIPSE_MANUAL_UPLOAD',
      'EAGLE_MANUAL_UPLOAD',
    ];
    return [
      {
        key: 'docNumber',
        label: <span className="flex text-nowrap">Document #</span>,
        width: 120,
        sticky: true,
        render: (_, row) => row?.docNumber || '-',
      },
      {
        key: 'type',
        label: <span className="pl-1">Type</span>,
        width: 80,
        getSortValue: (row) => {
          if (QuickbooksInvoiceSchema.safeParse(row).success) {
            const invoice = row as QuickbooksInvoice;
            if (invoice.recordType === 'FINANCE_CHARGE') return 'Finance Charge';
            if (invoice.recordType === 'DEBIT_MEMO') return 'Debit Memo';
            if (invoice.recordType === 'PAYMENT') return 'Payment';
            return 'Invoice';
          }
          if (QuickbooksCreditMemoSchema.safeParse(row).success) {
            const creditMemo = row as QuickbooksCreditMemo;
            if (creditMemo.recordType === 'PAYMENT') return 'Payment';
            if (creditMemo.recordType === 'MISC_CREDITS') return 'Misc Credits';
            if (creditMemo.recordType === 'CREDIT_INVOICE') return 'Credits Invoice';
            return 'Credit Memo';
          }
          return '';
        },
        render: (_, row) => {
          if (QuickbooksInvoiceSchema.safeParse(row).success) {
            const invoice = row as QuickbooksInvoice;
            if (invoice.recordType === 'FINANCE_CHARGE') return 'Finance Charge';
            if (invoice.recordType === 'DEBIT_MEMO') return 'Debit Memo';
            if (invoice.recordType === 'PAYMENT') return 'Payment';
            return 'Invoice';
          }
          if (QuickbooksCreditMemoSchema.safeParse(row).success) {
            const creditMemo = row as QuickbooksCreditMemo;
            if (creditMemo.recordType === 'PAYMENT') return 'Payment';
            if (creditMemo.recordType === 'MISC_CREDITS') return 'Misc Credits';
            if (creditMemo.recordType === 'CREDIT_INVOICE') return 'Credits Invoice';
            return 'Credit Memo';
          }
          return '-';
        },
      },
      {
        key: 'poNumber',
        label: 'PO #',
        width: 120,
        render: (_, row) => (
          <div className="flex overflow-hidden w-full px-0.5">
            <p className="text-xs TEXT_PRIMARY-CLR break-words line-clamp-3">
              {row?.poNumber || '-'}
            </p>
          </div>
        ),
      },
      {
        key: 'job',
        label: 'Job',
        width: 180,
        render: (_, row) => {
          const jobName = row?.quickBooksJob?.jobName || '';
          const jobAddress = `${row?.quickBooksJob?.address || ''} ${
            row?.quickBooksJob?.city || ''
          } ${row?.quickBooksJob?.state || ''}`.trim();
          return (
            <div className="text-sm w-full px-0.5">
              <p className="font-bold text-xs w-full truncate">{jobName}</p>
              {jobAddress && (
                <>
                  <p
                    data-tooltip-id={'job-address-' + row.id}
                    className="text-2xs w-full line-clamp-2 cursor-default">
                    {jobAddress}
                  </p>
                  <ReactTooltip
                    id={'job-address-' + row.id}
                    place="top"
                    variant="dark"
                    opacity="100%"
                    style={{ background: '#222', width: '240px', zIndex: 99999, fontSize: '9px' }}>
                    <span>{jobAddress}</span>
                  </ReactTooltip>
                </>
              )}
            </div>
          );
        },
      },
      {
        key: 'dueDate',
        label: 'Due Date',
        width: 120,
        render: (_, row) => {
          if (!row?.dueDate) return '-';
          const isPastDue = moment(row.dueDate).utc().isBefore(moment().utc(), 'day');
          return (
            <span className={isPastDue ? 'text-red-500' : ''}>
              {moment(row.dueDate).utc().format('MM/DD/YYYY')}
            </span>
          );
        },
      },
      {
        key: 'createdAt',
        label: 'Date',
        width: 100,
        render: (_, row) =>
          row?.createdAt ? moment(row.createdAt).utc().format('MM/DD/YYYY') : '-',
      },
      {
        key: 'terms',
        label: 'Terms',
        width: 120,
        render: (_, row) => {
          const invoiceCheck = QuickbooksInvoiceSchema.safeParse(row);
          return invoiceCheck.success ? invoiceCheck.data.terms || '-' : '-';
        },
      },
      {
        key: 'amount',
        label: 'Amount',
        width: 120,
        numeric: true,
        getSortValue: (row) => {
          const invoiceCheck = QuickbooksInvoiceSchema.safeParse(row);
          const creditMemoCheck = QuickbooksCreditMemoSchema.safeParse(row);
          if (invoiceCheck.success) {
            return parseFloat(invoiceCheck.data.amount?.toString() || '0');
          }
          if (creditMemoCheck.success) {
            const creditMemo = creditMemoCheck.data;
            if (
              creditMemo.recordType === 'CREDIT_MEMO' ||
              creditMemo.recordType === 'PAYMENT' ||
              creditMemo.recordType === 'MISC_CREDITS' ||
              creditMemo.recordType === 'CREDIT_INVOICE'
            ) {
              return -parseFloat(Math.abs(creditMemo.totalAmount).toString() || '0');
            }
          }
          return 0;
        },
        render: (_, row) => {
          const invoiceCheck = QuickbooksInvoiceSchema.safeParse(row);
          const creditMemoCheck = QuickbooksCreditMemoSchema.safeParse(row);
          let value = '';

          if (invoiceCheck.success) {
            value = `$${roundTo(parseFloat(invoiceCheck.data.amount?.toString() || '0'), 2)}`;
          } else if (creditMemoCheck.success) {
            const creditMemo = creditMemoCheck.data;
            if (
              creditMemo.recordType === 'CREDIT_MEMO' ||
              creditMemo.recordType === 'PAYMENT' ||
              creditMemo.recordType === 'MISC_CREDITS' ||
              creditMemo.recordType === 'CREDIT_INVOICE'
            ) {
              value = `-$${roundTo(
                parseFloat(Math.abs(creditMemo.totalAmount).toString() || '0'),
                2
              )}`;
            } else {
              value = '-';
            }
          }
          const inv = invoiceCheck.success ? invoiceCheck.data : null;
          const isCvsPlumbingDiscountTerm =
            supplier?.domainId === 'cvsplumbing' &&
            CVS_PLUMBING_DISCOUNT_TERMS.includes(inv?.terms ?? '');
          const isMarksManualUploadDiscount =
            supplier?.integrationType === 'MARKS_MANUAL_UPLOAD' &&
            !!inv?.invoiceDiscountAmount &&
            inv.invoiceDiscountAmount > 0 &&
            inv?.invoiceDiscountDate != null &&
            moment().isSameOrBefore(moment(inv.invoiceDiscountDate).endOf('day'));
          const discountAvailable =
            !!inv &&
            ((NET_TERMS_REGEX.test(inv.terms ?? '') &&
              DISCOUNT_INTEGRATIONS.includes(supplier?.integrationType ?? '')) ||
              isCvsPlumbingDiscountTerm ||
              isMarksManualUploadDiscount);
          const discountAmount = isMarksManualUploadDiscount
            ? inv?.invoiceDiscountAmount
            : inv?.dillInvoicePaymentSummary?.discountAmount;
          let discountTooltipText =
            'This invoice is eligible for an early payment discount based on its terms.';
          if (isMarksManualUploadDiscount && discountAmount) {
            const discountDate = moment(inv?.invoiceDiscountDate).format('MM/DD/YYYY');
            discountTooltipText = `Discount available: ${formatMoney(
              discountAmount,
              2
            )} if paid by ${discountDate}`;
          } else if (discountAmount) {
            discountTooltipText = `Discount available: ${formatMoney(discountAmount, 2)}`;
          }
          return (
            <div className="flex items-end justify-end flex-col w-full">
              <p className="text-xs TEXT_PRIMARY-CLR text-right w-full">{value}</p>
              {discountAvailable && (
                <>
                  <p
                    data-tooltip-id={'possible-discount-' + row.id}
                    className="text-[9px] text-right PRIMARY_500-CLR w-full cursor-default">
                    Possible Discount
                  </p>
                  <ReactTooltip
                    id={'possible-discount-' + row.id}
                    place="top"
                    variant="dark"
                    opacity="100%"
                    style={{ background: '#222', width: '240px', zIndex: 99999 }}>
                    <span>{discountTooltipText}</span>
                  </ReactTooltip>
                </>
              )}
            </div>
          );
        },
      },
      {
        key: 'balance',
        label: 'Open',
        width: 100,
        numeric: true,
        getSortValue: (row) => {
          const invoiceCheck = QuickbooksInvoiceSchema.safeParse(row);
          const creditMemoCheck = QuickbooksCreditMemoSchema.safeParse(row);
          let sortValue = 0;
          if (invoiceCheck.success) {
            sortValue = parseFloat(invoiceCheck.data.balance?.toString() || '0');
          }
          if (creditMemoCheck.success) {
            const creditMemo = creditMemoCheck.data;
            if (
              creditMemo.recordType === 'CREDIT_INVOICE' ||
              creditMemo.recordType === 'CREDIT_MEMO' ||
              creditMemo.recordType === 'PAYMENT' ||
              creditMemo.recordType === 'MISC_CREDITS'
            ) {
              sortValue = -parseFloat(creditMemo.balance?.toString() || '0');
            }
          }
          return sortValue;
        },
        render: (_, row) => {
          const invoiceCheck = QuickbooksInvoiceSchema.safeParse(row);
          const creditMemoCheck = QuickbooksCreditMemoSchema.safeParse(row);
          let value = '';
          if (invoiceCheck.success) {
            value = `$${roundTo(parseFloat(invoiceCheck.data.balance?.toString() || '0'), 2)}`;
          } else if (creditMemoCheck.success) {
            const creditMemo = creditMemoCheck.data;
            if (creditMemo.recordType === 'CREDIT_INVOICE') {
              value = `-$${roundTo(parseFloat(creditMemo.balance?.toString() || '0'), 2)}`;
            } else if (
              creditMemo.recordType === 'CREDIT_MEMO' ||
              creditMemo.recordType === 'PAYMENT' ||
              creditMemo.recordType === 'MISC_CREDITS'
            ) {
              value = `-$${roundTo(parseFloat(creditMemo.balance?.toString() || '0'), 2)}`;
            } else {
              value = '-';
            }
          }
          return value;
        },
      },
      {
        key: 'dillPaymentStatus',
        label: 'Status',
        width: 140,
        render: (_, row) => {
          const status = row?.dillPaymentStatus;
          return (
            <div
              className={twMerge(
                'flex items-center px-3 py-1 w-full rounded-2xl justify-center',
                status === 'RECONCILIATION_PENDING'
                  ? 'INFO_100-BG INFO_500-CLR'
                  : status === 'TRANSACTION_PENDING'
                  ? 'PRIMARY_100-BG PRIMARY_500-CLR'
                  : 'GREY_100-BG GREY_500-CLR'
              )}>
              <p className="text-2xs font-bold capitalize">
                {status === 'RECONCILIATION_PENDING'
                  ? 'Pending Reconciliation'
                  : status === 'TRANSACTION_PENDING'
                  ? 'Transaction Pending'
                  : 'Open'}
              </p>
            </div>
          );
        },
      },
      {
        key: 'activities',
        label: 'Activities',
        width: 220,
        sortable: false,
        render: (_, row) => {
          const invoiceCheck = QuickbooksInvoiceSchema.safeParse(row);
          if (invoiceCheck.success) {
            const invoice = invoiceCheck.data;
            const activitiesCount = invoice?.collectionActivities?.length ?? 0;

            // Best-effort preview of the most recent activity. Defensively guarded so a
            // malformed activity can never break the row render — worst case we just
            // show the count badge with no preview line, same as before.
            let latestActivityPreview: { dateLabel: string; summary: string } | null = null;
            if (activitiesCount > 0) {
              try {
                const sortedActivities = [...(invoice.collectionActivities ?? [])].sort(
                  (a, b) => new Date(b?.date ?? 0).getTime() - new Date(a?.date ?? 0).getTime()
                );
                const mostRecent = sortedActivities[0];
                const formatted = mostRecent ? formatCollectionActivityLine(mostRecent) : null;
                if (formatted?.summary) {
                  latestActivityPreview = {
                    dateLabel: formatted.date ? moment(formatted.date).format('MM/DD/YYYY') : '',
                    summary: formatted.summary,
                  };
                }
              } catch {
                latestActivityPreview = null;
              }
            }

            return (
              <div className="flex w-full items-center justify-start gap-2">
                <div className="relative shrink-0">
                  <DillButton
                    icon={<ListChecks color={COLORS.GREY_500} size={16} />}
                    onClick={() => {
                      setSelectedInvoice(invoice);
                      setOpenActivitiesModal(true);
                    }}
                    height={32}
                    width={32}
                    variant="icon-default"
                  />
                  {activitiesCount > 0 && (
                    <div className="flex absolute -top-[5px] right-[-10px] PRIMARY_500-BG WHITE-CLR rounded-full text-2xs font-bold h-5 w-5 items-center border border-white justify-center">
                      {activitiesCount}
                    </div>
                  )}
                </div>
                {latestActivityPreview && (
                  <div className="flex flex-col min-w-0 flex-1">
                    {latestActivityPreview.dateLabel && (
                      <p className="text-2xs text-gray-500 w-full truncate">
                        {latestActivityPreview.dateLabel}
                      </p>
                    )}
                    <p
                      className="text-xs text-gray-700 w-full truncate"
                      title={latestActivityPreview.summary}>
                      {latestActivityPreview.summary}
                    </p>
                  </div>
                )}
              </div>
            );
          }
          return null;
        },
      },
      {
        key: 'actions',
        label: '',
        width: 363,
        sortable: false,
        stickyRight: true,
        render: (_, row) => {
          const invoiceCheck = QuickbooksInvoiceSchema.safeParse(row);
          const creditMemoCheck = QuickbooksCreditMemoSchema.safeParse(row);
          const invoice = invoiceCheck.success ? invoiceCheck.data : null;
          const creditMemo = creditMemoCheck.success ? creditMemoCheck.data : null;
          const isPdfAvailable = invoice?.isPdfAvailable || creditMemo?.isPdfAvailable;

          return (
            <div className="flex w-full items-center h-full">
              <div className="flex w-full items-start gap-1">
                <div className={isPdfAvailable ? '' : 'invisible'}>
                  <DillButton
                    height={32}
                    width={32}
                    variant="icon-default"
                    icon={<Eye color={COLORS.GREY_500} size={16} />}
                    onClick={() => {
                      if (invoice) {
                        setSelectedInvoice(invoice);
                        if (invoice.isPdfAvailable) {
                          handlePreviewPdf({
                            id: invoice.id,
                            docNumber: invoice.docNumber,
                            type: 'INVOICE',
                            signedPdfs: invoice.dillInvoice?.signedPdfs,
                          });
                        } else {
                          handlePreviewInvoiceWithoutPdf(invoice);
                        }
                      }
                      if (creditMemo) {
                        setSelectedCreditMemo(creditMemo);
                        if (creditMemo.isPdfAvailable) {
                          handlePreviewPdf({
                            id: creditMemo.id,
                            docNumber: creditMemo.docNumber,
                            type: 'CREDIT_MEMO',
                          });
                        }
                      }
                    }}
                  />
                </div>
                <div className={isPdfAvailable ? '' : 'invisible'}>
                  <DillButton
                    height={32}
                    width={32}
                    variant="icon-default"
                    icon={<Download color={COLORS.GREY_500} fontWeight={300} size={16} />}
                    onClick={() => {
                      if (invoice && invoice.isPdfAvailable) {
                        handleDownloadPdf({
                          id: invoice.id,
                          type: 'INVOICE',
                          docNumber: invoice.docNumber,
                          signedPdfs: invoice.dillInvoice?.signedPdfs,
                        });
                      }
                      if (creditMemo && creditMemo.isPdfAvailable) {
                        handleDownloadPdf({
                          id: creditMemo.id,
                          type: 'CREDIT_MEMO',
                          docNumber: creditMemo.docNumber,
                        });
                      }
                    }}
                  />
                </div>
                {invoice ? (
                  <div>
                    <DillButton
                      height={32}
                      width={32}
                      variant="icon-default"
                      icon={<Warning color={COLORS.GREY_500} size={16} />}
                      onClick={() => {
                        setInvoiceForDispute(invoice);
                        setOpenCreateDisputeModal(true);
                      }}
                    />
                  </div>
                ) : (
                  <div />
                )}
                {invoice ? (
                  <div className="flex flex-col gap-0.5 relative">
                    {activePromisesByInvoice[invoice.id] && (
                      <div className="flex items-center gap-1 absolute right-0 top-[-8px]">
                        {activePromisesByInvoice[invoice.id].some((p) => p.status === 'ACTIVE') && (
                          <span className="text-2xs px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 font-medium">
                            Promise
                          </span>
                        )}
                        {activePromisesByInvoice[invoice.id].some((p) => p.status === 'BROKEN') && (
                          <span className="text-2xs px-1.5 py-0.5 rounded-full bg-red-100 text-red-700 font-medium">
                            Broken
                          </span>
                        )}
                      </div>
                    )}
                    <DillDropdownButton
                      label="Send Payment Reminder"
                      options={[
                        { label: 'Automatic Send', value: 'automatic' },
                        { label: 'Customize Send', value: 'customize' },
                      ]}
                      onSelect={(option) => {
                        if (option.value === 'automatic' && invoice?.id) {
                          setCurrentReminderType('PAYMENT');
                          handleSendInvoicePaymentReminder(invoice.id);
                        } else if (option.value === 'customize') {
                          setCurrentReminderType('PAYMENT');
                          setSelectedInvoiceForReminder(invoice);
                          setOpenCustomizeReminderModal(true);
                        }
                      }}
                      icon={<CaretDown color={COLORS.WHITE} size={15} />}
                      height={32}
                    />
                    {invoice?.lastEmailed && (
                      <p className="text-xs text-gray-400 text-center truncate w-full">
                        {`Last sent ${getCurrentTimezoneDate(invoice.lastEmailed, 'MM/DD/YYYY')}`}
                      </p>
                    )}
                  </div>
                ) : (
                  <div className="flex-1 min-w-0" />
                )}
                <DeletableItem item={invoice ?? creditMemo} />
              </div>
            </div>
          );
        },
      },
    ];
  }, [
    user,
    activePromisesByInvoice,
    handleSendInvoicePaymentReminder,
    handlePreviewPdf,
    handlePreviewInvoiceWithoutPdf,
    handleDownloadPdf,
  ]);

  const accumulateAmount = useMemo(() => {
    const flatInvoices = Object.values(selectedInvoices).flat();
    if (flatInvoices.length > 0) {
      const flatCreditMemos = Object.values(selectedCreditMemos).flat();
      const totalBalance = flatInvoices.reduce((acc, invoice) => acc + (invoice.balance ?? 0), 0);
      const creditAmount = flatCreditMemos.reduce(
        (acc, creditMemo) => acc + (creditMemo.balance ?? 0),
        0
      );
      return totalBalance - creditAmount;
    }
    return 0;
  }, [selectedInvoices, selectedCreditMemos]);

  const handlePayBulkInvoice = () => {
    setInvoicesToBePaid(Object.values(selectedInvoices).flat());
    setPayInvoicesModalOpen(true);
  };

  const getDocumentType = (
    invoice: QuickbooksInvoice | null,
    creditMemo: QuickbooksCreditMemo | null
  ): string => {
    if (invoice && invoice.recordType === 'FINANCE_CHARGE') {
      return 'Finance Charge';
    } else if (invoice && invoice.recordType === 'DEBIT_MEMO') {
      return 'Debit Memo';
    } else if (invoice && !invoice.recordType) {
      return 'Invoice';
    } else if (creditMemo && creditMemo.recordType === 'PAYMENT') {
      return 'Payment';
    } else if (creditMemo && creditMemo.recordType === 'MISC_CREDITS') {
      return 'Misc Credits';
    } else if (creditMemo && creditMemo.recordType === 'CREDIT_INVOICE') {
      return 'Credits Invoice';
    } else if (creditMemo) {
      return 'Credit Memo';
    } else {
      return '';
    }
  };

  const handleDeleteSupplierOpenItem = async (
    invoiceId: string,
    type?:
      | 'FINANCE_CHARGE'
      | 'DEBIT_MEMO'
      | 'INVOICE'
      | 'PAYMENT'
      | 'CREDIT_MEMO'
      | 'MISC_CREDITS'
      | 'CREDIT_INVOICE'
      | null
  ) => {
    let dataType = 'invoice';
    if (type) {
      dataType = type.toLowerCase().replace('_', ' ');
    }
    dispatch(
      openMessageModal({
        buttonText: 'Close',
        modalType: 'WARN',
        title: `Are you sure you would like to delete this ${dataType}?`,
        subTitle:
          'Doing so will delete it from the portal as well as any associated payments that you have imported on your side.',
        secondButtonText: 'Proceed',
        isSecondButton: true,
        onSecondButtonClick: async () => {
          const res = await dispatch(
            deleteSupplierInvoice({
              invoiceId: invoiceId,
              supplierId: currentSupplier?.id || '',
              type: dataType,
            })
          );
          if (res.type === 'invoice/deleteSupplierInvoice/fulfilled') {
            fetchData(selectedBuyer?.id ?? '');
          } else {
            openDillToast({ message: 'Failed to delete invoice', type: 'ERROR' });
          }
        },
      })
    );
  };

  const isCreditMemoSelectable = (
    creditMemo: QuickbooksCreditMemo,
    integrationType: string | undefined
  ) =>
    ((creditMemo.recordType === 'CREDIT_MEMO' || creditMemo.recordType === 'CREDIT_INVOICE') &&
      integrationType === 'P21') ||
    ((creditMemo.recordType === 'CREDIT_MEMO' ||
      creditMemo.recordType === 'CREDIT_INVOICE' ||
      creditMemo.recordType === 'PAYMENT') &&
      integrationType === 'SAGE_100') ||
    ((creditMemo.recordType === 'CREDIT_MEMO' ||
      creditMemo.recordType === 'MISC_CREDITS' ||
      creditMemo.recordType === 'PAYMENT') &&
      integrationType === 'INFOR_DIST_SX') ||
    ((creditMemo.recordType === 'CREDIT_MEMO' ||
      creditMemo.recordType === 'CREDIT_INVOICE' ||
      creditMemo.recordType === 'PAYMENT') &&
      integrationType === 'DISTRIBUTION_ONE') ||
    (creditMemo.recordType === 'CREDIT_MEMO' && integrationType === 'MANUAL_UPLOAD') ||
    ((creditMemo.recordType === 'CREDIT_MEMO' || creditMemo.recordType === 'CREDIT_INVOICE') &&
      integrationType === 'ABLE_MANUAL_UPLOAD') ||
    ((creditMemo.recordType === 'CREDIT_MEMO' || creditMemo.recordType === 'CREDIT_INVOICE') &&
      integrationType === 'P21_MANUAL_UPLOAD') ||
    ((creditMemo.recordType === 'CREDIT_MEMO' ||
      creditMemo.recordType === 'CREDIT_INVOICE' ||
      creditMemo.recordType === 'PAYMENT') &&
      integrationType === 'DDI_MANUAL_UPLOAD') ||
    ((creditMemo.recordType === 'CREDIT_MEMO' ||
      creditMemo.recordType === 'CREDIT_INVOICE' ||
      creditMemo.recordType === 'PAYMENT') &&
      integrationType === 'ECLIPSE_MANUAL_UPLOAD') ||
    ((creditMemo.recordType === 'CREDIT_MEMO' ||
      creditMemo.recordType === 'CREDIT_INVOICE' ||
      creditMemo.recordType === 'PAYMENT') &&
      integrationType === 'MARKS_MANUAL_UPLOAD') ||
    ((creditMemo.recordType === 'CREDIT_MEMO' ||
      creditMemo.recordType === 'CREDIT_INVOICE' ||
      creditMemo.recordType === 'PAYMENT') &&
      integrationType === 'RUTTER_QBD') ||
    ((creditMemo.recordType === 'CREDIT_MEMO' ||
      creditMemo.recordType === 'CREDIT_INVOICE' ||
      creditMemo.recordType === 'PAYMENT') &&
      integrationType === 'RUTTER_NETSUITE') ||
    ((creditMemo.recordType === 'CREDIT_MEMO' ||
      creditMemo.recordType === 'CREDIT_INVOICE' ||
      creditMemo.recordType === 'PAYMENT') &&
      integrationType === 'ECLIPSE') ||
    ((creditMemo.recordType === 'CREDIT_MEMO' ||
      creditMemo.recordType === 'CREDIT_INVOICE' ||
      creditMemo.recordType === 'PAYMENT') &&
      integrationType === 'EAGLE_MANUAL_UPLOAD') ||
    integrationType === 'QBO';

  const DeletableItem = ({ item }: { item: QuickbooksInvoice | QuickbooksCreditMemo | null }) => {
    if (
      item &&
      (currentSupplier?.integrationType === 'MANUAL_UPLOAD' ||
        currentSupplier?.domainId === 'topgun' ||
        currentSupplier?.integrationType === 'MARKS_MANUAL_UPLOAD' ||
        currentSupplier?.integrationType === 'ECLIPSE_MANUAL_UPLOAD') &&
      item.dillPaymentStatus !== 'TRANSACTION_PENDING' &&
      item.dillPaymentStatus !== 'RECONCILIATION_PENDING'
    ) {
      return (
        <div>
          <DillButton
            onClick={() => handleDeleteSupplierOpenItem(item?.id ?? '', item?.recordType)}
            icon={<TrashIcon />}
            variant="icon-red"
            height={32}
            width={32}
          />
        </div>
      );
    }
    return null;
  };

  useEffect(() => {
    setSortedInvoices([...filteredCurrentBuyerInvoices]);
    if (selectedInvoice) {
      const updatedInvoice = filteredCurrentBuyerInvoices.find(
        (inv) => inv.id === selectedInvoice.id
      );
      if (updatedInvoice && QuickbooksInvoiceSchema.safeParse(updatedInvoice).success) {
        setSelectedInvoice(QuickbooksInvoiceSchema.parse(updatedInvoice));
      }
    }
  }, [filteredCurrentBuyerInvoices]);

  const sortByProperty = (
    sortType: 'asc' | 'desc',
    sortKey: string,
    getter: (item: any) => any,
    compare?: (a: any, b: any) => number
  ) => {
    const filteredInvoices = filteredCurrentBuyerInvoices.filter((invoice) => {
      const value = getter(invoice);
      return value !== undefined && value !== null;
    });

    const sortedInvoicesCopy = [...filteredInvoices];
    sortedInvoicesCopy.sort((a, b) => {
      const valueA = getter(a);
      const valueB = getter(b);

      if (compare) {
        return compare(valueA, valueB) * (sortType === 'asc' ? 1 : -1);
      } else {
        const stringA = String(valueA).toUpperCase();
        const stringB = String(valueB).toUpperCase();

        if (stringA < stringB) {
          return sortType === 'asc' ? -1 : 1;
        } else if (stringA > stringB) {
          return sortType === 'asc' ? 1 : -1;
        }

        return 0;
      }
    });

    const undefinedOrNullInvoices = filteredCurrentBuyerInvoices.filter((invoice) => {
      const value = getter(invoice);
      return value === undefined || value === null;
    });

    const fnalInvoice = sortedInvoicesCopy.concat(undefinedOrNullInvoices);

    setSortedInvoices(fnalInvoice);
  };

  const activeJobIds = selectedJobIds.filter((id) => id !== 'ALL');
  const activeJobs = selectedJobs.filter((j) => activeJobIds.includes(j.id));

  const jobFilteredSummary = useMemo(() => {
    if (activeJobIds.length === 0) return null;
    const openInvoices = currentBuyerInvoices.filter(
      (inv) =>
        QuickbooksInvoiceSchema.safeParse(inv).success &&
        inv.quickBooksJob &&
        activeJobIds.includes(inv.quickBooksJob.id) &&
        inv.dillPaymentStatus !== 'TRANSACTION_PENDING' &&
        (inv.dillPaymentStatus !== 'RECONCILIATION_PENDING' || (inv.balance && inv.balance > 0))
    );
    const todayStr = moment().format('YYYY-MM-DD');
    const dueDateStr = (d: Date | string) => moment(d).format('YYYY-MM-DD');
    const dpd = (d: Date | string) => moment(todayStr).diff(moment(dueDateStr(d)), 'days');
    return {
      totalAmountDue: openInvoices.reduce((s, inv) => s + (inv.balance ?? 0), 0),
      totalCurrentDue: openInvoices
        .filter((inv) => inv.dueDate && dueDateStr(inv.dueDate) >= todayStr)
        .reduce((s, inv) => s + (inv.balance ?? 0), 0),
      totalPastDue1To30: openInvoices
        .filter((inv) => inv.dueDate && dpd(inv.dueDate) > 0 && dpd(inv.dueDate) <= 30)
        .reduce((s, inv) => s + (inv.balance ?? 0), 0),
      totalPastDue31To60: openInvoices
        .filter((inv) => inv.dueDate && dpd(inv.dueDate) > 30 && dpd(inv.dueDate) <= 60)
        .reduce((s, inv) => s + (inv.balance ?? 0), 0),
      totalPastDue61To90: openInvoices
        .filter((inv) => inv.dueDate && dpd(inv.dueDate) > 60 && dpd(inv.dueDate) <= 90)
        .reduce((s, inv) => s + (inv.balance ?? 0), 0),
      totalPastDueOver90: openInvoices
        .filter((inv) => inv.dueDate && dpd(inv.dueDate) > 90)
        .reduce((s, inv) => s + (inv.balance ?? 0), 0),
    };
  }, [activeJobIds, currentBuyerInvoices]);

  const handleDownloadInvoiceStatements = async () => {
    const filteredInvoiceIds =
      activeJobIds.length > 0
        ? currentBuyerInvoices
            .filter(
              (inv) =>
                QuickbooksInvoiceSchema.safeParse(inv).success &&
                inv.quickBooksJob &&
                activeJobIds.includes(inv.quickBooksJob.id)
            )
            .map((inv) => inv.id)
        : undefined;
    const res = await dispatch(
      supplierDownloadSupplierInvoiceStatements({
        supplierId: currentSupplier?.id || '',
        buyerId: selectedBuyer?.id || '',
        ...(activeJobIds.length > 0 ? { jobIds: activeJobIds } : {}),
        ...(filteredInvoiceIds ? { invoiceIds: filteredInvoiceIds } : {}),
      })
    );
    if (res && res.type === 'invoice/supplierDownloadSupplierInvoiceStatements/fulfilled') {
      openDillToast({ type: 'SUCCESS', message: 'Invoice statement download success' });
    } else {
      openDillToast({ type: 'ERROR', message: 'Invoice statement download failed' });
    }
  };

  const handleSendEmailStatements = async (
    buyerIds: string[],
    statementId?: string,
    additionalEmails?: string[],
    excludedUserIds?: string[],
    customization?: {
      subjectLine?: string;
      notes?: string;
      attachments?: File[];
    }
  ) => {
    const jobExtras =
      activeJobIds.length > 0
        ? {
            jobIds: activeJobIds,
            jobNames: activeJobs.map(
              (j) => `${j.jobNumber ? `${j.jobNumber} - ` : ''}${j.jobName}`
            ),
            ...(jobFilteredSummary ? { jobsSummary: jobFilteredSummary } : {}),
          }
        : {};
    const recipientExtras = {
      ...(additionalEmails?.length ? { additionalEmails } : {}),
      ...(excludedUserIds?.length ? { excludedUserIds } : {}),
    };
    const customizationExtras = {
      ...(customization?.subjectLine ? { subjectLine: customization.subjectLine } : {}),
      ...(customization?.notes ? { notes: customization.notes } : {}),
      ...(customization?.attachments?.length ? { attachments: customization.attachments } : {}),
    };
    let results;
    if (statementId && currentSupplier?.id) {
      results = await dispatch(
        sendEmailStatementsForBuyersWithStatementId({
          buyerIds,
          statementId,
          supplierId: currentSupplier.id,
          ...jobExtras,
          ...recipientExtras,
          ...customizationExtras,
        })
      );
    } else {
      results = await dispatch(
        sendEmailStatementsForBuyers({
          supplierId: currentSupplier?.id || '',
          buyerIds,
          ...jobExtras,
          ...recipientExtras,
          ...customizationExtras,
        })
      );
    }
    const fulfilledType = statementId
      ? 'supplierBuyers/sendEmailStatementsForBuyersWithStatementId/fulfilled'
      : 'supplier/sendEmailStatementsForBuyers/fulfilled';
    if (results.type === fulfilledType) {
      openDillToast({ type: 'SUCCESS', message: 'Individual statement sent!' });
    } else {
      const payload = results.payload as any;
      let errorMessage = 'Error sending individual statement!';
      if (payload) {
        errorMessage = payload;
      }
      openDillToast({ type: 'ERROR', message: errorMessage });
    }
  };

  const validInvoices = sortedInvoices.filter(
    (documentDetails) =>
      QuickbooksInvoiceSchema.safeParse(documentDetails).success &&
      documentDetails.dillPaymentStatus !== 'TRANSACTION_PENDING' &&
      (documentDetails.dillPaymentStatus !== 'RECONCILIATION_PENDING' ||
        (documentDetails.balance && documentDetails.balance > 0))
  );

  const statementPdfsOptions = useMemo(() => {
    let allOptions: { label: string; value: string }[] = [];
    if (selectedBuyer?.historicalStatements && selectedBuyer?.historicalStatements.length > 0) {
      allOptions = [{ name: 'Today', url: 'TODAY' }, ...selectedBuyer?.historicalStatements].map(
        (item) => {
          return {
            label:
              item.name && item.url !== 'TODAY'
                ? item.name.split('__')[0]
                : item.url === 'TODAY'
                ? item.name || ''
                : '',
            value: item.url || '',
            extraData: item.name || '',
          };
        }
      );
    }
    return allOptions;
  }, [selectedBuyer]);

  const sendStatementLabel =
    activeJobIds.length === 1
      ? 'Send Job Statement'
      : activeJobIds.length > 1
      ? 'Send Jobs Statement'
      : 'Send Statement';

  const downloadStatementLabel =
    activeJobIds.length === 1
      ? 'Download Job Statement'
      : activeJobIds.length > 1
      ? 'Download Jobs Statement'
      : 'Download Statement';

  const toolbarRight = (
    <div className="flex items-start gap-4">
      <div className="flex flex-col">
        {statementPeriods.length > 0 && activeJobIds.length === 0 ? (
          <DillDropdownButton
            label={sendStatementLabel}
            icon={<CaretDown color={COLORS.WHITE} size={15} />}
            options={[
              { label: 'Current', value: 'CURRENT' },
              ...statementPeriods.map((sp) => ({
                label: moment(sp.statementDate).utc().format('MM-DD-YYYY'),
                value: sp.id,
              })),
            ]}
            onSelect={(option) => {
              const statementId = option.value === 'CURRENT' ? undefined : option.value;
              setPendingStatementData({
                buyerIds: [customerDetails?.buyer?.id ?? ''],
                statementId,
              });
              setOpenSendStatementModal(true);
            }}
          />
        ) : (
          <DillButton
            label={sendStatementLabel}
            icon={<Mail size={15} />}
            onClick={() => {
              setPendingStatementData({ buyerIds: [customerDetails?.buyer?.id ?? ''] });
              setOpenSendStatementModal(true);
            }}
          />
        )}
        {customerDetails?.buyer?.lastEmailed && (
          <p className="text-2xs text-gray-400 text-center mt-1">{`Current statement last sent ${getCurrentTimezoneDate(
            customerDetails.buyer.lastEmailed,
            'MM/DD/YYYY'
          )}`}</p>
        )}
      </div>
      <div className="">
        {statementPdfsOptions.length > 0 && activeJobIds.length === 0 && (
          <DillDropdownButton
            options={[...statementPdfsOptions]}
            variant="secondary"
            onSelect={(option) => {
              if (option.value === 'TODAY') {
                handleDownloadInvoiceStatements();
              } else {
                if (option.value) {
                  let fileName = option.extraData;
                  if (!fileName) {
                    const decodedUrl = decodeURIComponent(option.value);
                    const len = decodedUrl.split('/');
                    fileName = `${len[len.length - 1]}.pdf`;
                  }
                  downloadFile(option.value, fileName);
                }
              }
            }}
            label={downloadStatementLabel}
          />
        )}
        {(statementPdfsOptions.length === 0 || activeJobIds.length > 0) && (
          <DillButton
            label={downloadStatementLabel}
            variant="secondary"
            icon={<FilePlus size={15} />}
            onClick={handleDownloadInvoiceStatements}
          />
        )}
      </div>
      <div className="relative grid justify-items-end">
        <div className="relative">
          <DillDropdownButton
            label="Send Bulk Reminder"
            variant="secondary"
            options={[
              { label: 'Automatic Send', value: 'automatic' },
              { label: 'Customize Send', value: 'customize' },
            ]}
            onSelect={(option) => {
              if (option.value === 'automatic') {
                setCurrentReminderType('PAYMENT');
                handleSendBulkInvoicePaymentReminder('PAYMENT');
              } else if (option.value === 'customize') {
                setCurrentReminderType('PAYMENT');
                setOpenBulkCustomizeReminderModal(true);
              }
            }}
            icon={<CaretDown size={15} />}
            isDisabled={
              Object.values(selectedInvoices).flat() &&
              Object.values(selectedInvoices).flat().length < 1
            }
          />
          <div
            className={twMerge(
              'flex absolute -top-2 right-[-12px] PRIMARY_500-BG WHITE-CLR rounded-full text-2xs font-bold h-6 w-6 items-center border border-white justify-center',
              Object.values(selectedInvoices).flat() &&
                Object.values(selectedInvoices).flat().length < 1
                ? 'opacity-70'
                : 'opacity-100'
            )}>
            {Object.values(selectedInvoices).flat()?.length}
          </div>
        </div>
      </div>
      <div
        className="relative grid justify-items-end mr-2"
        data-tooltip-id={
          Object.values(selectedInvoices).flat() &&
          Object.values(selectedInvoices).flat().length < 1
            ? 'supplier-bulk-charge-tool-tip'
            : ''
        }>
        <div className="relative">
          <DillButton
            label="Bulk Charge"
            variant="secondary"
            onClick={handlePayBulkInvoice}
            isDisabled={
              !currentSupplier?.isFullyVerified ||
              (Object.values(selectedInvoices).flat() &&
                Object.values(selectedInvoices).flat().length < 1)
            }
          />
          <div
            className={twMerge(
              'flex absolute -top-2 right-[-12px] PRIMARY_500-BG WHITE-CLR rounded-full text-2xs font-bold h-6 w-6 items-center border border-white justify-center',
              Object.values(selectedInvoices).flat() &&
                Object.values(selectedInvoices).flat().length < 1
                ? 'opacity-70'
                : 'opacity-100'
            )}>
            {Object.values(selectedInvoices).flat()?.length}
          </div>
        </div>
        <div className="TEXT_SECONDARY-CLR mt-1 ml-2 text-sm ">
          {formatMoney(accumulateAmount, 2)}
        </div>
        <ReactTooltip
          id={'supplier-bulk-charge-tool-tip'}
          place="left"
          variant="dark"
          opacity={'100%'}
          style={{
            background: '#222',
            width: '200px',
            zIndex: 99999,
          }}>
          <span>Multi-select items to bulk charge</span>
        </ReactTooltip>
      </div>
    </div>
  );
  return (
    <div className="flex flex-col w-full h-full pt-4">
      {payInvoicesModalOpen && currentSupplier && customerDetails && (
        <AppSelectedPayInvoicesModal
          open={payInvoicesModalOpen}
          title="Bulk Charge"
          handleClose={() => {
            setInvoicesToBePaid([]);
            setPayInvoicesModalOpen(false);
            if (selectedBuyer) {
              setCurrentBuyerInvoices([]);
              fetchData(selectedBuyer?.id);
            }
          }}
          selectedInvoices={invoicesToBePaid}
          paymentTo={currentSupplier?.name?.toLocaleLowerCase()}
          paymentFrom={selectedBuyer?.name.toLocaleLowerCase()}
          selectedCreditMemos={Object.values(selectedCreditMemos).flat()}
          supplierChargeDetails={{ customerDetails, supplier: currentSupplier }}
        />
      )}
      <AppPdfPreviewModal
        open={openPdfPreviewModal}
        handleClose={() => {
          setOpenPdfPreviewModal(false);
          setSelectedCreditMemo(null);
          setSelectedInvoice(null);
        }}
        invoice={
          selectedInvoice
            ? selectedInvoice?.docNumber || ''
            : selectedCreditMemo
            ? selectedCreditMemo?.docNumber || ''
            : ''
        }
        pdfType={selectedInvoice ? 'INVOICE' : selectedCreditMemo ? 'CREDIT_MEMO' : undefined}
        pdfList={previewPdfList}
        handleDownloadPdf={handleDownloadSelectedPdf}
        isSigned={false}
        selectedItem={selectedInvoice || selectedCreditMemo || null}
      />
      <InvoiceActivitiesModal
        open={openActivitiesModal}
        handleClose={() => {
          setOpenActivitiesModal(false);
        }}
        invoiceId={selectedInvoice?.id || ''}
        invoiceDocNumber={selectedInvoice?.docNumber || ''}
        invoiceAmount={selectedInvoice?.amount}
        buyerId={selectedBuyer?.id || ''}
        supplierId={currentSupplier?.id || ''}
        activities={selectedInvoice?.collectionActivities || []}
        onActivityChanged={() => {
          if (selectedBuyer) {
            fetchData(selectedBuyer.id);
          }
        }}
      />
      {isViewInvoiceWithoutPdfModalOpen && (
        <AppViewInvoiceWithoutPdfModal
          open={isViewInvoiceWithoutPdfModalOpen}
          handleClose={() => {
            setIsViewInvoiceWithoutPdfModalOpen(false);
          }}
          selectedInvoice={selectedInvoice}
          selectedSupplier={currentSupplier}
        />
      )}
      <AppSendStatementModal
        open={openSendStatementModal}
        handleClose={() => {
          setOpenSendStatementModal(false);
          setPendingStatementData(null);
        }}
        onSubmit={async ({
          excludedUserIds,
          additionalEmails,
          subjectLine,
          notes,
          attachments,
        }) => {
          setSendStatementLoading(true);
          await handleSendEmailStatements(
            pendingStatementData?.buyerIds ?? [],
            pendingStatementData?.statementId,
            additionalEmails,
            excludedUserIds,
            { subjectLine, notes, attachments }
          );
          setSendStatementLoading(false);
          setOpenSendStatementModal(false);
          setPendingStatementData(null);
        }}
        loading={sendStatementLoading}
        buyerUsers={customerDetails?.usersEmailsDetails ?? []}
      />
      <CreateDisputeModal
        open={openCreateDisputeModal}
        onClose={() => {
          setOpenCreateDisputeModal(false);
          setInvoiceForDispute(null);
        }}
        buyerId={customerDetails?.buyer.id}
        invoice={invoiceForDispute}
      />
      <AppCustomizePaymentReminderModal
        open={openCustomizeReminderModal}
        handleClose={() => {
          setOpenCustomizeReminderModal(false);
          setSelectedInvoiceForReminder(null);
          setCurrentReminderType('PAYMENT');
        }}
        onSubmit={(data) => handleSendCustomizedPaymentReminder(data, data.reminderType)}
        loading={customReminderLoading}
        selectedInvoices={selectedInvoiceForReminder ? [selectedInvoiceForReminder] : []}
        defaultReminderType={currentReminderType}
        buyerUsers={customerDetails?.usersEmailsDetails ?? []}
        buyer={selectedBuyer}
      />
      <AppCustomizePaymentReminderModal
        open={openBulkCustomizeReminderModal}
        handleClose={() => {
          setOpenBulkCustomizeReminderModal(false);
          setCurrentReminderType('PAYMENT');
        }}
        onSubmit={(data) => handleSendBulkCustomizedPaymentReminder(data, data.reminderType)}
        loading={bulkReminderLoading}
        selectedInvoices={Object.values(selectedInvoices).flat()}
        defaultReminderType={currentReminderType}
        buyerUsers={customerDetails?.usersEmailsDetails ?? []}
        buyer={selectedBuyer}
      />
      <DillTable
        columns={columns}
        data={sortedInvoices}
        selectable={true}
        onSelectionChange={handleSelectionChange}
        selectedRowIds={
          new Set([
            ...Object.values(selectedInvoices)
              .flat()
              .map((inv) => inv.id),
            ...Object.values(selectedCreditMemos)
              .flat()
              .map((cm) => cm.id),
          ])
        }
        scrollX
        sortable
        striped={false}
        rowHeight={70}
        emptyMessage="No invoices"
        countLabel="Invoices"
        showCount={false}
        onRowClick={(row) => {
          const invoiceCheck = QuickbooksInvoiceSchema.safeParse(row);
          if (invoiceCheck.success) {
            setSelectedInvoice(invoiceCheck.data);
          }
        }}
        isRowSelectable={(row) => {
          const invoice = QuickbooksInvoiceSchema.safeParse(row);
          if (invoice.success) {
            const isPendingTransactionOrReconciliation =
              invoice.data.dillPaymentStatus === 'TRANSACTION_PENDING' ||
              invoice.data.dillPaymentStatus === 'RECONCILIATION_PENDING';
            if (isPendingTransactionOrReconciliation) return false;
            return true;
          }
          const creditMemo = QuickbooksCreditMemoSchema.safeParse(row);
          if (!creditMemo.success) return false;

          const isPendingTransactionOrReconciliation =
            creditMemo.data.dillPaymentStatus === 'TRANSACTION_PENDING' ||
            creditMemo.data.dillPaymentStatus === 'RECONCILIATION_PENDING';
          if (isPendingTransactionOrReconciliation) return false;

          return isCreditMemoSelectable(
            creditMemo.data,
            currentSupplier?.integrationType ?? undefined
          );
        }}
        getRowDisabledReason={(row) => {
          const invoice = QuickbooksInvoiceSchema.safeParse(row);
          if (invoice.success) {
            if (invoice.data.dillPaymentStatus === 'TRANSACTION_PENDING') {
              return 'This invoice has a pending transaction and cannot be selected.';
            }
            if (invoice.data.dillPaymentStatus === 'RECONCILIATION_PENDING') {
              return 'This invoice is pending reconciliation and cannot be selected.';
            }
            return undefined;
          }
          const creditMemo = QuickbooksCreditMemoSchema.safeParse(row);
          if (creditMemo.success) {
            if (creditMemo.data.dillPaymentStatus === 'TRANSACTION_PENDING') {
              return 'This credit memo has a pending transaction and cannot be selected.';
            }
            if (creditMemo.data.dillPaymentStatus === 'RECONCILIATION_PENDING') {
              return 'This credit memo is pending reconciliation and cannot be selected.';
            }
          }
          return undefined;
        }}
        toolbarRight={toolbarRight}
        // maxHeight="calc(100vh - 480px)"
        searchClassName="h-9"
      />
    </div>
  );
};

export default CustomerInvoicesTable;
